import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * On-site approval DNE override (Lance, 3 Nov). Job Ops owns the rule and the record; the approve itself is V1's Job-chat
 * approve (JobOpsChat/PostOnsiteEstimateAction with dneOverrideRemark, or PostDneOverrideManagerApprove for the manager).
 */

export type DneOverrideType = 'ReducedProfit' | 'ManagerUpline';

export interface DneOverrideSummary {
  overrideKey: string;
  estimateKey: string;
  jobKey: string;
  vendorKey: string;
  overrideType: DneOverrideType;
  status: string;
  vendorTotal: number;
  customerDne: number;
  profitMarginPercent: number | null;
  floorMarginPercent: number;
  overrideRemark: string | null;
  dneReason: string | null;
  requestedByName: string | null;
  requestedOn: string;
  uplineEmailSentTo: string | null;
  managerDecision: string | null;
  managerDecidedByName: string | null;
  managerDecidedOn: string | null;
  managerRemark: string | null;
  approvedOn: string | null;
  approvalFailureReason: string | null;
  closedReason: string | null;
}

export interface DneOverrideEvaluation {
  estimateKey: string;
  keepAllVendors: boolean;
  blocked: boolean;
  overrideType: DneOverrideType | null;
  estimateTotal: number;
  otherVendorsTotal: number;
  vendorTotal: number;
  customerDne: number;
  minimumMarkupPercent: number | null;
  markupRuleLabel: string | null;
  requiredTotal: number | null;
  minimumProfitAmount: number | null;
  profitAmount: number;
  profitMarginPercent: number | null;
  floorMarginPercent: number;
  vendorAtOrAboveDne: boolean;
  popupMessage: string | null;
  atOrAboveDneWarning: string | null;
  callManagerMessage: string | null;
  managerRecipientCount: number;
  openOverride: DneOverrideSummary | null;
}

export interface DneOverrideReturned {
  overrideKey: string;
  jobKey: string;
  vendorKey: string;
  estimateKey: string;
  po: string | null;
  managerName: string | null;
  managerRemark: string | null;
  decidedOn: string | null;
}

/** A job + vendor waiting for an uplining manager; managerNames = the managers whose link still works. */
export interface DneOverridePending {
  overrideKey: string;
  jobKey: string;
  vendorKey: string;
  estimateKey: string;
  po: string | null;
  managerNames: string[];
  requestedByName: string | null;
  requestedOn: string;
  sentOn: string | null;
}

export interface DneOverrideLine {
  kind: string;
  itemName: string | null;
  description: string | null;
  qty: number;
  rate: number;
  total: number;
}

export interface DneOverrideForm {
  canDecide: boolean;
  notice: string | null;
  status: string;
  managerName: string | null;
  po: string | null;
  jobName: string | null;
  customerName: string | null;
  vendorName: string | null;
  locationLine: string | null;
  isEmergencyJob: boolean;
  requestedByName: string | null;
  requestedByEmail: string | null;
  requestedOn: string;
  expiresOn: string;
  overrideRemark: string;
  dneReason: string | null;
  vendorEstimateTotal: number;
  otherVendorsTotal: number;
  keepAllVendors: boolean;
  vendorTotal: number;
  customerRevisedDne: number;
  minimumMarkupPercent: number | null;
  markupRuleLabel: string | null;
  requiredTotal: number | null;
  minimumProfitAmount: number | null;
  profitAmount: number;
  profitMarginPercent: number | null;
  floorMarginPercent: number;
  vendorAtOrAboveDne: boolean;
  vendorEstimate: { title: string | null; total: number; lines: DneOverrideLine[] } | null;
  customerEstimateDraft: { available: boolean; unavailableReason: string | null; total: number; lines: DneOverrideLine[] } | null;
  managerDecision: string | null;
  managerDecidedByName: string | null;
  managerDecidedOn: string | null;
  managerRemark: string | null;
  approvalFailureReason: string | null;
}

/** Every call answers this: ok with data, or the server's message (and error code when it sent one). */
export interface DneOverrideResult<T> {
  ok: boolean;
  data: T | null;
  message: string;
  code: string | null;
}

/** Job Ops sends these UTC times without a zone ("2026-11-03T14:13:14"): mark them UTC so the date pipe shows local time. */
const NAIVE_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;
export function asUtc<T>(value: T): T {
  if (typeof value === 'string') return (NAIVE_ISO.test(value) ? `${value}Z` : value) as T;
  if (Array.isArray(value)) return value.map((v) => asUtc(v)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = asUtc(v);
    return out as T;
  }
  return value;
}

interface Envelope<T> {
  status?: boolean;
  message?: string;
  data?: T;
  details?: { code?: string; message?: string }[];
}

@Injectable({ providedIn: 'root' })
export class DneOverrideService {
  private readonly http = inject(HttpClient);
  private readonly adminBase = `${environment.apiBaseUrl}/api/v2/admin/dne-override`;
  private readonly publicBase = `${environment.apiBaseUrl}/api/public/dne-override`;
  private readonly legacyBase = environment.legacyAdminBaseUrl.replace(/\/$/, '');

  evaluate(estimateKey: string, keepAll: boolean): Observable<DneOverrideResult<DneOverrideEvaluation>> {
    const params = new HttpParams().set('estimateKey', estimateKey).set('keepAll', String(keepAll));
    return this.wrap(this.http.get<Envelope<DneOverrideEvaluation>>(`${this.adminBase}/evaluate`, { params }));
  }

  upline(body: {
    estimateKey: string;
    keepAllVendors: boolean;
    remark: string;
    dneReason?: string;
  }): Observable<DneOverrideResult<{ overrideKey: string; status: string; sentTo: string | null; message: string }>> {
    return this.wrap(this.http.post<Envelope<{ overrideKey: string; status: string; sentTo: string | null; message: string }>>(`${this.adminBase}/upline`, body), 60_000);
  }

  /** Sends a request still waiting for a manager again (fresh links; the earlier ones stop working). */
  resend(overrideKey: string): Observable<DneOverrideResult<{ overrideKey: string; status: string; sentTo: string | null; message: string }>> {
    return this.wrap(
      this.http.post<Envelope<{ overrideKey: string; status: string; sentTo: string | null; message: string }>>(`${this.adminBase}/${overrideKey}/resend`, {}),
      60_000,
    );
  }

  cancel(overrideKey: string, reason?: string): Observable<DneOverrideResult<DneOverrideSummary>> {
    return this.wrap(this.http.post<Envelope<DneOverrideSummary>>(`${this.adminBase}/${overrideKey}/cancel`, { reason: reason ?? null }));
  }

  pending(): Observable<DneOverrideResult<DneOverridePending[]>> {
    return this.wrap(this.http.get<Envelope<DneOverridePending[]>>(`${this.adminBase}/pending`));
  }

  returned(): Observable<DneOverrideResult<DneOverrideReturned[]>> {
    return this.wrap(this.http.get<Envelope<DneOverrideReturned[]>>(`${this.adminBase}/returned`));
  }

  markSeen(overrideKey: string): Observable<DneOverrideResult<DneOverrideReturned>> {
    return this.wrap(this.http.post<Envelope<DneOverrideReturned>>(`${this.adminBase}/${overrideKey}/seen`, {}));
  }

  /** The manager's form (no sign-in: the token is the credential, sent in the body). */
  form(token: string): Observable<DneOverrideResult<DneOverrideForm>> {
    return this.wrap(this.http.post<Envelope<DneOverrideForm>>(`${this.publicBase}/form`, { token }));
  }

  editNeeded(token: string, remark: string): Observable<DneOverrideResult<{ status: string; message: string }>> {
    return this.wrap(this.http.post<Envelope<{ status: string; message: string }>>(`${this.publicBase}/edit-needed`, { token, remark }), 60_000);
  }

  /** The manager approves: V1 runs the same approve as a Job-chat approve, as that manager. */
  managerApprove(token: string, remark: string): Observable<DneOverrideResult<null>> {
    return this.http
      .post<{ ok?: boolean; message?: string; error?: string }>(`${this.legacyBase}/JobOpsChat/PostDneOverrideManagerApprove`, { token, remark })
      .pipe(
        timeout(120_000),
        map((res) => ({
          ok: res?.ok === true,
          data: null,
          message: res?.message || (res?.ok ? 'Approved.' : 'The approval did not go through.'),
          code: res?.error ?? null,
        })),
        catchError(() =>
          of({ ok: false, data: null, message: 'Could not reach the server. Check your connection and try again.', code: 'network_error' }),
        ),
      );
  }

  private wrap<T>(req: Observable<Envelope<T>>, ms = 30_000): Observable<DneOverrideResult<T>> {
    return req.pipe(
      timeout(ms),
      map((res) => ({
        ok: res?.status !== false && res?.data != null,
        data: asUtc(res?.data ?? null) as T | null,
        message: res?.message ?? '',
        code: res?.details?.[0]?.code ?? null,
      })),
      catchError((err: unknown) => {
        const body = err instanceof HttpErrorResponse ? (err.error as Envelope<T> | null) : null;
        const message =
          body?.message ||
          (err instanceof HttpErrorResponse && err.status === 0
            ? 'Could not reach the server. Check your connection and try again.'
            : 'The request failed. Please try again.');
        return of({ ok: false, data: null, message, code: body?.details?.[0]?.code ?? null });
      }),
    );
  }
}
